import { useMemo } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { importTypes, settingStepFields } from '../../config/import';
import {
  importClients,
  importIndividualMeters,
  importPersonalAccounts,
  importPersonalAccountsByCompanies,
} from './requests';
import { selectors, actions } from '../../model';
import { uploadingFieldNames } from '../../config/uploadingStep';
import {
  createErrorHeaders,
  createRows,
  createWorkbook,
  downloadBlobFile,
} from '../../lib/file';

const { selectUploadingStepValue } = selectors;
const { resetStateAction, addResultData } = actions;

const importTypeSelector = (state) =>
  selectUploadingStepValue(state, uploadingFieldNames.importType);

const mutationConfigs = {
  [importTypes.owners]: {
    mutationFn: ({ items, houseId }) =>
      importClients({ houseId, data: { items } }),
    getErrorItems: (data) => data?.errorItems,
    fields: settingStepFields[importTypes.owners],
  },

  [importTypes.personalAccounts]: {
    mutationFn: importPersonalAccounts,
    getErrorItems: (data) =>
      data?.reduce((acc, item) => {
        if (!item.isSuccess) {
          acc.push(item.subject);
        }
        return acc;
      }, []),
    fields: settingStepFields[importTypes.personalAccounts],
  },

  [importTypes.personalAccountsByCompanies]: {
    mutationFn: importPersonalAccountsByCompanies,
    getErrorItems: () => [],
    fields: settingStepFields[importTypes.personalAccountsByCompanies],
  },

  [importTypes.individualMeters]: {
    mutationFn: importIndividualMeters,
    getErrorItems: (data) =>
      data?.reduce((acc, item) => {
        if (!item.isSuccess) {
          const { errorDescription, subject } = item;
          acc.push({ ...subject, errorDescription });
        }
        return acc;
      }, []),
    fields: settingStepFields[importTypes.individualMeters],
  },
};

const onSuccess = ({ errorFile, errorItems, dispatch, fields }) => {
  if (errorFile) {
    downloadBlobFile(errorFile);
    dispatch(
      addResultData({
        title: 'Загрузка завершена с ошибками',
        errorsFile: errorFile,
      })
    );
    return;
  }

  if (!errorItems?.length) {
    dispatch(resetStateAction());
    message.success('Данные загружены');
    return;
  }

  const headers = createErrorHeaders(fields);
  const rows = createRows({ data: errorItems, fields, transform: true });
  const errorsWorkbook = createWorkbook({ rows, headers });

  dispatch(addResultData({ errorsCount: errorItems.length, errorsWorkbook }));
};

export const useSubmit = () => {
  const importType = useSelector(importTypeSelector);

  const dispatch = useDispatch();

  const { mutationFn, getErrorItems, fields } = mutationConfigs[importType];

  const options = useMemo(
    () => ({
      onSuccess: (data) => {
        if (data !== 'Error') {
          const errorItems = getErrorItems(data);
          onSuccess({
            errorFile: data?.errorFile,
            errorItems,
            fields,
            dispatch,
          });
        }
      },
    }),
    [dispatch, getErrorItems, fields]
  );

  const mutation = useMutation(mutationFn, options);

  return mutation;
};
